import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  LinearProgress,
  Chip,
  Card,
  CardContent,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  CloudUpload,
  InsertDriveFile,
  Delete,
  Description,
  CheckCircle,
  ExpandMore,
  PictureAsPdf,
  Image,
  Code,
  ArticleOutlined,
  AttachFile
} from '@mui/icons-material';
import uarService from '../../../services/UARService';
import { formatFileSize } from '../../../utils/formatUtils';

const UARCACheckForm = ({ uarFile, onComplete, onCancel }) => {
  const [caCheck, setCACheck] = useState(null);
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);

  // Get file icon based on file type
  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    
    if (['pdf'].includes(extension)) {
      return <PictureAsPdf color="error" />;
    } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension)) {
      return <Image color="primary" />;
    } else if (['doc', 'docx', 'txt', 'rtf'].includes(extension)) {
      return <Description color="primary" />;
    } else if (['xls', 'xlsx', 'csv'].includes(extension)) {
      return <ArticleOutlined style={{ color: '#4caf50' }} />;
    } else if (['js', 'jsx', 'ts', 'tsx', 'html', 'css', 'json', 'xml'].includes(extension)) {
      return <Code color="secondary" />;
    } else {
      return <AttachFile />;
    }
  };

  // Load existing C&A check data if available
  useEffect(() => {
    const fetchCACheck = async () => {
      try {
        if (uarFile?.id) {
          const data = await uarService.fetchUARCACheck(uarFile.id);
          if (data) {
            setCACheck(data);
            setDescription(data.DESCRIPTION || '');
            
            // Fetch evidence files
            const evidence = await uarService.getUARCACheckEvidence(data.id);
            if (Array.isArray(evidence)) {
              setUploadedFiles(evidence);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching C&A check data:', error);
        setError('Failed to load C&A check data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchCACheck();
  }, [uarFile]);

  // Dropzone setup for file uploads
  const onDrop = useCallback(acceptedFiles => {
    setFiles(prevFiles => [...prevFiles, ...acceptedFiles]);
  }, []);
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ 
    onDrop,
    multiple: true
  });

  // Remove a file from the list before upload
  const removeFile = (index) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  // Delete an uploaded file
  const deleteUploadedFile = async (evidenceId) => {
    try {
      await uarService.deleteUARCACheckEvidence(evidenceId);
      setUploadedFiles(prevFiles => prevFiles.filter(file => file.id !== evidenceId));
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error deleting file:', error);
      setError('Failed to delete file. Please try again.');
      setTimeout(() => setError(null), 5000);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    setError(null);
    
    try {
      // Create or update the C&A check record
      let checkId;
      
      if (!caCheck) {
        // Create new C&A check
        const caCheckData = {
          UAR_FILE: uarFile.id,
          DESCRIPTION: description,
          STATUS: 'Pending'
        };
        
        const newCACheck = await uarService.createUARCACheck(caCheckData);
        checkId = newCACheck.id;
        setCACheck(newCACheck);
      } else {
        // Update existing C&A check
        const updateData = {
          DESCRIPTION: description
        };
        
        await uarService.updateUARCACheck(uarFile.id, updateData);
        checkId = caCheck.id;
      }
      
      // Upload files if any
      if (files.length > 0) {
        setUploading(true);
        
        await uarService.uploadUARCACheckEvidence(
          checkId, 
          files,
          (progress) => setUploadProgress(progress)
        );
        
        // Refresh the uploaded files list
        const evidence = await uarService.getUARCACheckEvidence(checkId);
        if (Array.isArray(evidence)) {
          setUploadedFiles(evidence);
        }
        
        setFiles([]);
        setUploading(false);
        setUploadProgress(0);
      }
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving C&A check:', error);
      setError('Failed to save C&A check. Please try again.');
      setUploading(false);
    }
  };

  // Handle completion of C&A check
  const handleComplete = async () => {
    try {
      if (!caCheck) {
        setError('Please save the C&A check information first.');
        return;
      }
      
      if (uploadedFiles.length === 0) {
        setError('Please upload at least one evidence file before completing the C&A check.');
        return;
      }
      
      // Update the C&A check status to Completed
      await uarService.updateUARCACheck(uarFile.id, {
        STATUS: 'Completed'
      });
      
      setSuccess(true);
      
      // Notify parent component that C&A check is complete
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('Error completing C&A check:', error);
      setError('Failed to complete C&A check. Please try again.');
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Changes saved successfully!
        </Alert>
      )}
      
      <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Completeness & Accuracy Check
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Before proceeding to the Segregation of Duties review, you must verify the completeness and accuracy of the user list.
            Please provide your comments on the completeness check and upload any evidence documents.
          </Typography>
        </CardContent>
      </Card>
      
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Completeness Check Comments
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe the completeness and accuracy check you performed. Include any findings or observations..."
          variant="outlined"
          sx={{ mb: 2 }}
        />
      </Paper>
      
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Upload Evidence
        </Typography>
        
        <Box
          {...getRootProps()}
          sx={{
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'divider',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            mb: 3,
            backgroundColor: isDragActive ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
              cursor: 'pointer'
            }
          }}
        >
          <input {...getInputProps()} />
          <CloudUpload color="primary" sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="subtitle1" gutterBottom>
            {isDragActive ? 'Drop the files here' : 'Drag and drop files here, or click to select files'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Supported file types: PDF, Word, Excel, images, text files
          </Typography>
        </Box>
        
        {files.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Files to Upload ({files.length})
            </Typography>
            <List>
              {files.map((file, index) => (
                <ListItem key={index} sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1 }}>
                  <ListItemIcon>
                    {getFileIcon(file.name)}
                  </ListItemIcon>
                  <ListItemText
                    primary={file.name}
                    secondary={`${formatFileSize(file.size)} | ${file.type || 'Unknown type'}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => removeFile(index)} size="small">
                      <Delete color="error" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
            
            {uploading && (
              <Box sx={{ width: '100%', mt: 2 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                  Uploading... {Math.round(uploadProgress)}%
                </Typography>
              </Box>
            )}
          </Box>
        )}
        
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
          Uploaded Evidence Files ({uploadedFiles.length})
        </Typography>
        
        {uploadedFiles.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No evidence files have been uploaded yet. Please upload at least one file to complete the C&A check.
          </Alert>
        ) : (
          <List>
            {uploadedFiles.map((file, index) => (
              <ListItem key={index} sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1 }}>
                <ListItemIcon>
                  {getFileIcon(file.FILE_NAME)}
                </ListItemIcon>
                <ListItemText
                  primary={file.FILE_NAME}
                  secondary={`Uploaded on ${new Date(file.CREATED_ON).toLocaleDateString()}`}
                />
                <ListItemSecondaryAction>
                  <IconButton edge="end" onClick={() => deleteUploadedFile(file.id)} size="small">
                    <Delete color="error" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
        {onCancel && (
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
        )}
        
        <Button 
          variant="contained" 
          color="primary"
          onClick={handleSubmit}
          disabled={uploading}
        >
          Save Changes
        </Button>
        
        <Button
          variant="contained"
          color="success"
          startIcon={<CheckCircle />}
          onClick={handleComplete}
          disabled={uploading || uploadedFiles.length === 0 || (caCheck?.STATUS === 'Completed')}
        >
          Complete C&A Check
        </Button>
      </Box>
    </Box>
  );
};

export default UARCACheckForm; 